import { useReactFlow } from '@xyflow/react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { DEFAULT_FONT_SIZE, DEFAULT_TEXT_WIDTH } from '../../nodes/Text/constants';
import { useEditCanvasStore } from '../../store';
import { type AppNode } from '../../types';
import { useClipboardStore } from '../clipboard/clipboardStore';
import { type ClipboardItem } from '../clipboard/types';
import { computeBoundingBox, prepareObjectsForPaste } from '../clipboard/utils';
import { commands } from '../commands';
import { useCommandHistory } from '../commands/hooks/useCommandHistory';
import { useDropFilesToCanvas } from '../useDropFilesToCanvas';
import { useSelectedCanvasNodes } from '../useSelectedCanvasNodes';

/** Custom MIME type written to the OS clipboard during copy/cut so paste can
 *  detect whether the last copy originated from the canvas or externally. */
const CANVAS_CLIPBOARD_MIME = 'application/x-air-canvas-node';

/** Returns true if an editable element (input/textarea/contenteditable) is focused. */
function isEditableElement() {
  const active = document.activeElement;

  return (
    active instanceof HTMLInputElement ||
    active instanceof HTMLTextAreaElement ||
    active?.getAttribute('contenteditable') === 'true'
  );
}

/** Returns true if native clipboard handling should take precedence — i.e. an
 *  input/textarea/contenteditable is focused, or the user has selected text. */
function isNativeClipboardContext() {
  if (isEditableElement()) {
    return true;
  }

  // A non-empty window selection means the user highlighted text (e.g. in the
  // chat sidebar) — let the browser handle copy/cut natively.
  const selection = window.getSelection();

  return !!selection && !selection.isCollapsed;
}

export function useClipboardShortcuts() {
  const { getViewport, screenToFlowPosition } = useReactFlow();
  const selectedNodes = useSelectedCanvasNodes();
  const execute = useCommandHistory((state) => state.execute);
  const setClipboard = useClipboardStore((state) => state.setClipboard);
  const clipboard = useClipboardStore((state) => state.clipboard);
  const readOnly = useEditCanvasStore((state) => state.readOnly);
  const { dropFilesToCanvas } = useDropFilesToCanvas();
  const { resolvedTheme } = useTheme();

  // Track paste count for offset calculation
  const pasteCountRef = useRef(0);
  const lastClipboardRef = useRef<ClipboardItem | null>(null);

  const handleCopy = useCallback(() => {
    if (selectedNodes.length === 0) {
      return;
    }

    const clipboardItem: ClipboardItem = {
      boundingBox: computeBoundingBox(selectedNodes),
      nodes: structuredClone(selectedNodes),
    };

    setClipboard(clipboardItem);
    pasteCountRef.current = 0;
    lastClipboardRef.current = clipboardItem;
  }, [selectedNodes, setClipboard]);

  const handlePaste = useCallback(() => {
    if (!clipboard) {
      return;
    }

    // Reset paste count if clipboard changed
    if (clipboard !== lastClipboardRef.current) {
      pasteCountRef.current = 0;
      lastClipboardRef.current = clipboard;
    }

    // Use original object positions as paste base (paste in place with offset)
    const pastePosition = {
      x: clipboard.boundingBox.minX,
      y: clipboard.boundingBox.minY,
    };

    const { nodes: objects, groups } = prepareObjectsForPaste(clipboard, pastePosition, pasteCountRef.current);

    execute(commands.pasteNodes(objects, groups));

    pasteCountRef.current += 1;
  }, [clipboard, execute]);

  const handleCut = useCallback(() => {
    if (selectedNodes.length === 0) {
      return;
    }

    // Copy first
    const clipboardItem: ClipboardItem = {
      boundingBox: computeBoundingBox(selectedNodes),
      nodes: structuredClone(selectedNodes),
    };
    setClipboard(clipboardItem);
    pasteCountRef.current = 0;
    lastClipboardRef.current = clipboardItem;

    // Remove nodes via commands for undo support
    execute(commands.removeNodes(selectedNodes));
  }, [selectedNodes, setClipboard, execute]);

  const handleDuplicate = useCallback(() => {
    if (selectedNodes.length === 0) {
      return;
    }

    const clipboardItem: ClipboardItem = {
      boundingBox: computeBoundingBox(selectedNodes),
      nodes: structuredClone(selectedNodes),
    };

    // Use object positions as paste base (duplicate in place with offset)
    const pastePosition = {
      x: clipboardItem.boundingBox.minX,
      y: clipboardItem.boundingBox.minY,
    };

    const { nodes: objects, groups } = prepareObjectsForPaste(clipboardItem, pastePosition, 0);

    execute(commands.pasteNodes(objects, groups));
  }, [selectedNodes, execute]);

  const options = useMemo(() => ({ enabled: !readOnly, preventDefault: true }), [readOnly]);

  // Duplicate: Cmd+D / Ctrl+D (stays as hotkey — no OS clipboard involvement)
  useHotkeys('meta+d,ctrl+d', () => handleDuplicate(), options, [handleDuplicate, options]);

  // Copy & cut: native event listeners so we can write a custom MIME marker to
  // clipboardData, letting paste distinguish canvas copies from external ones.
  useEffect(() => {
    const onCopy = (event: ClipboardEvent) => {
      if (readOnly || isNativeClipboardContext()) {
        return;
      }

      handleCopy();

      // Only write the marker when there were selected nodes to copy.
      if (selectedNodes.length > 0) {
        event.preventDefault();
        event.clipboardData?.setData(CANVAS_CLIPBOARD_MIME, '1');
      }
    };

    const onCut = (event: ClipboardEvent) => {
      if (readOnly || isNativeClipboardContext()) {
        return;
      }

      // handleCut does copy + remove internally
      if (selectedNodes.length > 0) {
        event.preventDefault();
        event.clipboardData?.setData(CANVAS_CLIPBOARD_MIME, '1');
      }

      handleCut();
    };

    document.addEventListener('copy', onCopy);
    document.addEventListener('cut', onCut);
    return () => {
      document.removeEventListener('copy', onCopy);
      document.removeEventListener('cut', onCut);
    };
  }, [handleCopy, handleCut, readOnly, selectedNodes.length]);

  // Paste: native event listener so we can read clipboardData (images/text)
  // without triggering a browser permission prompt.
  const handlePasteText = useCallback(
    (text: string) => {
      const trimmed = text.trim();

      if (!trimmed) {
        return;
      }

      const { zoom } = getViewport();
      const fontSize = Math.round(DEFAULT_FONT_SIZE / zoom);
      const baseWidth = Math.round(DEFAULT_TEXT_WIDTH / zoom);

      const container = document.querySelector('.react-flow');
      const rect = container?.getBoundingClientRect();
      const position = screenToFlowPosition({
        x: rect ? rect.left + rect.width / 2 : window.innerWidth / 2,
        y: rect ? rect.top + rect.height / 2 : window.innerHeight / 2,
      });

      const fontColor = resolvedTheme === 'dark' ? '#ffffff' : '#000000';

      const nodeId = crypto.randomUUID();
      const newNode: AppNode = {
        data: {
          baseWidth,
          fontColor,
          fontFamily: 'Inter',
          fontSize,
          source: 'manual-edit',
          text: trimmed,
          textAlign: 'left',
          type: 'text',
        },
        id: nodeId,
        position,
        style: { width: baseWidth },
        type: 'text',
      };

      execute(commands.addNodes([newNode]));
    },
    [execute, getViewport, screenToFlowPosition, resolvedTheme],
  );

  useEffect(() => {
    const handler = (event: ClipboardEvent) => {
      if (readOnly || isEditableElement()) {
        return;
      }

      // 1. Image files from clipboard (highest priority)
      const files = Array.from(event.clipboardData?.files ?? []);
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));

      if (imageFiles.length > 0) {
        event.preventDefault();
        dropFilesToCanvas(imageFiles);
        return;
      }

      // 2. Internal clipboard (copied canvas nodes) — only if the last copy
      //    originated from the canvas (custom MIME marker is present).
      const hasCanvasClipboardMarker = !!event.clipboardData?.getData(CANVAS_CLIPBOARD_MIME);

      if (hasCanvasClipboardMarker && clipboard) {
        event.preventDefault();
        handlePaste();
        return;
      }

      // 3. Plain text from clipboard
      const text = event.clipboardData?.getData('text/plain');

      if (text) {
        event.preventDefault();
        handlePasteText(text);
      }
    };

    document.addEventListener('paste', handler);
    return () => document.removeEventListener('paste', handler);
  }, [clipboard, dropFilesToCanvas, handlePaste, handlePasteText, readOnly]);

  return {
    handleCopy,
    handleCut,
    handleDuplicate,
    handlePaste,
  };
}
